import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Notice } from "@/components/ui/notice";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { useToast } from "@/components/ui/toast";
import { create } from "../api/progress-reports-api";

interface CreateProgressReportFormProps {
  iepId: number;
  onCreated: () => void;
  onCancel: () => void;
}

export function CreateProgressReportForm({
  iepId,
  onCreated,
  onCancel,
}: CreateProgressReportFormProps) {
  const { t } = useTranslation(["progress-reports", "common"]);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { show } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await create(iepId, {
        reportingPeriodStart: periodStart || null,
        reportingPeriodEnd: periodEnd || null,
        notes: notes.trim() || undefined,
      });
      if (response.success) {
        show({ message: t("createForm.createdToast"), variant: "success" });
        onCreated();
      } else {
        setError(response.message || t("createForm.createFailed"));
      }
    } catch {
      setError(t("createForm.createError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4"
      data-testid="create-progress-report-form"
    >
      {error && <Notice variant="error" title={error} />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label={t("createForm.periodStart")}
          type="date"
          value={periodStart}
          onChange={(e) => setPeriodStart(e.target.value)}
          data-testid="pr-period-start"
        />
        <Input
          label={t("createForm.periodEnd")}
          type="date"
          value={periodEnd}
          onChange={(e) => setPeriodEnd(e.target.value)}
          data-testid="pr-period-end"
        />
      </div>

      <RichTextEditor
        label={t("createForm.notes")}
        value={notes}
        onChange={setNotes}
        minRows={3}
        placeholder={t("createForm.notesPlaceholder")}
        data-testid="pr-notes"
      />

      <div className="flex gap-2 justify-end">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t("common:ui.cancel")}
        </Button>
        <Button type="submit" loading={isSubmitting} data-testid="pr-submit">
          {t("createForm.create")}
        </Button>
      </div>
    </form>
  );
}
